import { GENDER, RELEASE_ITEM, VISIT_MODALITY } from "./ccm-consent-constant";
import {
  ConsentOption,
  ConsentTextBlock,
  ReleaseItem,
  VisitModality,
} from "./ccm-consent-types";

interface PrivacyNoticeSection {
  title: string;
  blocks: readonly ConsentTextBlock[];
}

export const PRIVACY_NOTICE_SECTIONS: readonly PrivacyNoticeSection[] = [
  {
    title: "Patient Health Information (PHI)",
    blocks: [
      {
        id: "phi-notice",
        lead: "This notice describes how medical information about you may be used disclosed, and how you can get access to this information. Please review this document carefully.",
        paragraphs: [
          "Under federal law, your patient health information (PHI) is protected and confidential. Patient health information (PHI) includes information about your symptoms, test results, diagnosis, treatment, and related medical information. Your patient health information (PHI) also includes payment, billing and insurance information. We are committed to protect the privacy of your PHI.",
        ],
      },
    ],
  },
  {
    title: "How we use your patient health information (PHI)",
    blocks: [
      {
        id: "notice-of-privacy-practices",
        paragraphs: [
          "This Notice of Privacy Practices (Notice) describes how we may use within our practice or network and disclose (share outside of our practice or network) your PHI to carry out treatment, payment or health care operations, for administrative purposes, for evaluation of the quality of care, and so forth. We may also share your PHI for other purposes that are permitted or required by law. This Notice also describes your rights to access and control your PHI. Under some circumstances we may be required to use or disclose your PHI without your consent.",
        ],
      },
      {
        id: "treatment",
        heading: "Treatment",
        paragraphs: [
          "We will use and disclose your PHI to provide you with medical treatment or services. We may also disclose your PHI to other health care providers who are participating in your treatment, to pharmacists who are filling in your prescriptions, to laboratories performing tests, and to family members who are helping with your care, and so forth.",
        ],
      },
      {
        id: "payment",
        heading: "Payment",
        paragraphs: [
          "We will use and disclose your PHI for payment purposes. For example, we may need to obtain authorization from your insurance company before providing certain types of treatment. We will submit bills and maintain records of payments from your health plan. PHI may be shared with the following: billing companies, insurance companies (health plans), government agencies in order to assist with qualifications of benefits, or collection agencies.",
        ],
      },
      {
        id: "operation",
        heading: "Operation",
        paragraphs: [
          "We may ask you to complete a check-in sheet or staff members may ask you the reason for your visit so we can better care for you. Despite safeguards, it is always possible in a doctor's office that you may learn information regarding other patients, or they may inadvertently learn something about you. In all cases we expect and request that our patients maintain strict confidentiality of PHI. We may use and disclose your PHI to perform various routine functions (e.g. Quality evaluations or records analysis, training students, other health care providers or ancillary staff such as billing personnel, to assist in resolving problems or complaints within the practice). We may use your PHI to contact you to provide information about referrals, for follow-up with lab results, to inquire about your health or for other reasons.",
          "We may share your PHI with Business Associates who assist us in performing routine operational functions, but we will always obtain assurance from them to protect your PHI the same as we do.",
        ],
      },
      {
        id: "special-situations",
        heading: "Special Situations that DO NOT require your permission",
        paragraphs: [
          "We may be required by law to report gunshot wounds, suspected abuse or neglect, and so on; we may be required to disclose vital statistics, diseases, and similar information to public health authorities; we may be required to disclose information for audits and similar activities, in response to a subpoena or court order, or as required by law enforcement officials. We may release information about you for workers' compensation or similar programs to protect your health or the health of others or for legitimate government needs, for approved medical research, or to certain entities in the case of death. Your PHI may also be shared if you are an inmate or under custody of the law which is necessary for your health or the health and safety of other individuals.",
        ],
      },
      {
        id: "military-activity",
        heading: "Military Activity and National Security",
        paragraphs: [
          "When the appropriate conditions apply, we may use or disclose PHI of individuals who are Armed Forces personnel for activities deemed necessary by appropriate military command authorities, for the purpose of a determination by the Department of Veterans affairs of your eligibility for benefits, or to foreign military authority if you are a member of that foreign military services.",
          "In some situations, we may ask for your written authorization before using or disclosing any identifiable health information about you. If you sign an authorization, you can later revoke the authorization.",
        ],
      },
    ],
  },
  {
    title: "Individual Rights",
    blocks: [
      {
        id: "rights-regarding-phi",
        heading: "You have certain rights regarding your PHI, for example",
        paragraphs: [
          "Unless you object, we may share your PHI with friends or family members, or other persons directly identified by you at the level they are involved in your care or payment of services. If you are not present or able to agree/object, the healthcare provider using professional judgment will determine if it is in your best interest to share the information. We may use or disclose PHI to notify or assist in notifying a family member, personal representative or any other person that is responsible for your care of your location, general condition or death. We may use or disclose your PHI to an authorized public or private entity to assist in disaster relief efforts.",
          "You may request restrictions on certain uses and disclosures of your PHI. We are not required to accept all restrictions. If you pay in full for a treatment or service immediately, you can request that we not share this information with your medical insurance provider or our Business Associates. We will make every attempt to accommodate this request and, if we cannot, we will tell you prior to the treatment.",
          "You may ask us to communicate with you confidentially by, for example, sending notices to a special address.",
          "In most cases, you have the right to get a copy of your PHI. There will be a charge for the copies.",
          "If you believe information in your record is incorrect, or if important information is missing, you have the right to request that we amend the existing information by submitting a written request. You may request a list of instances where we have disclosed PHI about you for reasons other than treatment, payment, or operations. The first request in a 12 month period is free. There will be charges for additional reports.",
          "You have the right to obtain a paper copy of this Notice from us, upon request. We will provide you a copy of this Notice on the first day we treat you at our facility. In an emergency situation we will give you this Notice as soon as possible. You have the right to receive notification of any breach of your protected health information.",
        ],
      },
    ],
  },
  {
    title: "Our Legal Duty",
    blocks: [
      {
        id: "legal-duty",
        paragraphs: [
          "We are required by law to protect and maintain the privacy of your PHI, to provide this Notice about our legal duties and privacy practices regarding PHI, and to abide by the terms of the Notice currently in effect. We may update or change our privacy practices and policies at any time. Before we make a significant change in our policies, we will change our Notice and post the new Notice in the admissions area and on our website. You can also request a copy of our Notice at any time.",
          "If you are concerned about your privacy rights, or if you disagree with a decision we made about your records, you may contact the Privacy Officer listed below. You may also send a written complaint to the U.S. Department of Health and Human Services. You will not be penalized in any way for filing a complaint",
        ],
      },
    ],
  },
];

export const GENDER_OPTIONS = [
  { value: GENDER.MALE, label: "Male" },
  { value: GENDER.FEMALE, label: "Female" },
] as const;

export const RELEASE_ITEM_OPTIONS: ConsentOption<ReleaseItem>[] = [
  { value: RELEASE_ITEM.HISTORY_AND_PHYSICALS, label: "History & Physicals" },
  {
    value: RELEASE_ITEM.CURRENT_MEDICAL_FINDINGS,
    label: "Current Medical Findings",
  },
  {
    value: RELEASE_ITEM.MEDICATION_LIST,
    label: "Previous and Present Medication list",
  },
  {
    value: RELEASE_ITEM.LAB_AND_IMAGING_REPORTS,
    label: "Lab and imaging Reports",
  },
  {
    value: RELEASE_ITEM.CHART_NOTES_AND_TREATMENT_PLAN,
    label: "Chart Notes/ Progress Reports & Treatment Plan, (if determined)",
  },
];

export const VISIT_MODALITY_OPTIONS: ConsentOption<VisitModality>[] = [
  { value: VISIT_MODALITY.AUDIO_VIDEO, label: "Audio-video" },
  { value: VISIT_MODALITY.AUDIO_ONLY, label: "Audio-only" },
  { value: VISIT_MODALITY.OTHER, label: "Other" },
];

export const TELEHEALTH_BLOCKS: readonly ConsentTextBlock[] = [
  {
    id: "documented-discussion",
    heading: "Documented discussion:",
    paragraphs: [
      "The patient was informed that telehealth uses real-time electronic communication to deliver care when the patient and practitioners are not in the same location. Medicare telehealth generally uses 2-way, interactive audio-video technology. CMS also permits certain audio-only telehealth in limited circumstances, including when the practitioner is capable of using audio-video and the patient is at home but is not capable of, or does not consent to, using video technology.",
    ],
  },
  {
    id: "items-reviewed",
    heading: "Items reviewed with patient",
    bullets: [
      "The visit will be provided by telehealth instead of an in-person encounter, when clinically appropriate.",
      "Telehealth may involve privacy, technology, or transmission risks, although reasonable safeguards are used to protect health information.",
      "The patient may ask questions at any time and may request an in-person visit when clinically appropriate or available.",
      "Medicare or other insurance may cover telehealth services, but deductible, coinsurance, or other cost-sharing may still apply.",
      "Emergency symptoms should be directed to 911 or the nearest emergency department rather than managed solely through telehealth.",
    ],
  },
];

export const CONSENT_CHECKBOX_TEXT = {
  TELEHEALTH_VERBAL:
    "Patients verbally consented to receive telehealth / telemedicine services and to proceed with today's remote visit.",
  ELECTRONIC_SMS:
    "Electronic / SMS Consent Obtained (Consent information was provided to the patient via SMS/text message)",
  VERBAL: "Verbal Consent Obtained",
} as const;

export const ELECTRONIC_CONSENT_LINES = [
  "Patients affirmatively responded by indicating agreement to:",
  "Telehealth services",
  "Electronic communication (including SMS/text messaging)",
  "Response documented in the medical record.",
] as const;

export const VERBAL_CONSENT_LINES = [
  "Patient verbally consented to:",
  "Receive telehealth services",
  "Verbal consent obtained after discussion of risks, benefits, and alternatives.",
  "Consent documented per CMS requirements.",
] as const;

export const PHONE_DIGIT_COUNT = 10;
export const NON_DIGIT_REGEX = /\D/g;

export const PREFILLED_PATIENT_FIELD_NAMES: readonly string[] = [
  "patient.firstName",
  "patient.lastName",
  "patient.dateOfBirth",
  "patient.address",
  "patient.city",
  "patient.state",
  "patient.zipCode",
  "patient.race",
  "patient.preferredLanguage",
  "emergencyContact.name",
  "emergencyContact.cell",
  "emergencyContact.relationshipToPatient",
];

export const EXPIRY_FIELD_NAME = "releaseAuthorization.expiry";

export const CONSENT_VALIDATION_TEXT = {
  SIGNATURE_REQUIRED: "Signature is required",
  EXPIRY_REQUIRED: "Select when this authorization expires",
  SIGNATORY_NAME_REQUIRED: "Name of the person signing is required",
  RELATIONSHIP_REQUIRED: "Relationship to patient is required",
} as const;

export const CCM_DEFAULT_EXPIRY_MONTHS = 12;
export const EXPIRY_NOTE_DATE_FORMAT = "MMMM D, YYYY";
export const CONSENT_EXPIRY_TEXT = {
  PERIOD_BEFORE_DATE:
    "This authorization is good for a period of {months} months. It will expire on ",
  PERIOD_AFTER_DATE: ".",
  NEVER:
    "This authorization has no expiration date and will remain in effect until revoked in writing.",
} as const;
